import React, { useMemo } from 'react';
import { View } from 'react-native';

import { ThemedText } from '@/components/themed-text';
import { PressableScale } from '@/components/ui/pressable-scale';
import { zonedWeekday } from '@/utils/format-appointment';
import { useThemedHex } from '@/hooks/use-theme-colors';

const WEEKDAY_HEADERS = ['S', 'T', 'Q', 'Q', 'S', 'S', 'D'];
const CELL_HEIGHT = 48;
const MAX_DOTS = 3;

interface Props {
  /** Dias do mês, em ordem ('YYYY-MM-DD'). */
  days: string[];
  selectedDay: string;
  todayKey: string;
  countByDay: Map<string, number>;
  conflictDays: Set<string>;
  blockedDays: Set<string>;
  onSelectDay: (day: string) => void;
}

/**
 * Visão Mês da agenda do prestador: grade com a carga de cada dia (um ponto por
 * agendamento, até 3). Tocar escolhe o dia cuja lista aparece abaixo.
 */
export function AgendaMonthGrid({
  days,
  selectedDay,
  todayKey,
  countByDay,
  conflictDays,
  blockedDays,
  onSelectDay,
}: Props) {
  const th = useThemedHex();
  // Semana começa na segunda, igual à visão Semana.
  const cells = useMemo(() => {
    const offset = (zonedWeekday(`${days[0]}T12:00:00-03:00`) + 6) % 7;
    const list: (string | null)[] = [...Array.from({ length: offset }, () => null), ...days];
    while (list.length % 7 !== 0) list.push(null);
    return list;
  }, [days]);

  return (
    <View
      className="rounded-2xl bg-card p-3"
      style={{ borderCurve: 'continuous', boxShadow: '0 1px 2px rgba(23,17,10,0.05)' }}
    >
      <View className="flex-row pb-1">
        {WEEKDAY_HEADERS.map((label, index) => (
          <View key={index} className="items-center" style={{ width: `${100 / 7}%` }}>
            <ThemedText className="text-[11px] font-semibold uppercase text-neutral-400">
              {label}
            </ThemedText>
          </View>
        ))}
      </View>

      <View className="flex-row flex-wrap">
        {cells.map((day, index) => {
          if (!day) {
            return (
              <View key={`empty-${index}`} style={{ width: `${100 / 7}%`, height: CELL_HEIGHT }} />
            );
          }
          const count = countByDay.get(day) ?? 0;
          const selected = day === selectedDay;
          const isToday = day === todayKey;
          const isPast = day < todayKey;
          const dayNumber = Number(day.slice(8));
          const dotColor = conflictDays.has(day) ? '#DC2626' : '#FD5001';

          return (
            <View
              key={day}
              className="items-center"
              style={{ width: `${100 / 7}%`, height: CELL_HEIGHT }}
            >
              <PressableScale
                haptic
                onPress={() => onSelectDay(day)}
                className="items-center gap-1 pt-1"
                accessibilityLabel={`Dia ${dayNumber}${count > 0 ? `, ${count} marcados` : ''}`}
              >
                <View
                  className="h-8 w-8 items-center justify-center rounded-full"
                  style={{
                    backgroundColor: selected ? '#FD5001' : 'transparent',
                    borderWidth: !selected && isToday ? 1 : 0,
                    borderColor: th('#D4D4D4'),
                  }}
                >
                  <ThemedText
                    className="text-sm"
                    style={{
                      color: selected ? '#fff' : isPast ? th('#A3A3A3') : th('#171717'),
                      fontWeight: selected || count > 0 ? '700' : '400',
                    }}
                  >
                    {dayNumber}
                  </ThemedText>
                </View>
                <View className="h-1.5 flex-row gap-0.5">
                  {count > 0
                    ? Array.from({ length: Math.min(count, MAX_DOTS) }, (_, i) => (
                        <View
                          key={i}
                          className="h-1.5 w-1.5 rounded-full"
                          style={{ backgroundColor: dotColor }}
                        />
                      ))
                    : blockedDays.has(day) && (
                        <View className="h-1.5 w-1.5 rounded-full bg-neutral-300" />
                      )}
                </View>
              </PressableScale>
            </View>
          );
        })}
      </View>
    </View>
  );
}
